import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GeolocationCoords } from '../../hooks/useGeolocation';
import { getCachedCoords, saveCoordsToCache } from '../../utils/geoCache';
import { useStorefront } from '../../hooks/useStorefront';
import { StorefrontService } from '../../types/additionalServiceTypes';
import LocationGate from './components/LocationGate';
import StorefrontFilters from './components/StorefrontFilters';
import ServiceList from './components/ServiceList';
import { AppHeader } from '../../components/shared/AppHeader';

function VitrinePage() {
  const navigate = useNavigate();

  const [coords, setCoords] = useState<GeolocationCoords | null>(() => getCachedCoords());

  useEffect(() => {
    document.title = 'Serviços Adicionais — WashPass';
    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content =
        'Descubra serviços adicionais disponíveis para assinantes WashPass na sua região.';
    }
    return () => {
      document.title = 'WashPass';
    };
  }, []);

  const { filteredServices, loading, error, filters, setFilters } = useStorefront(coords);

  const handleLocationResolved = useCallback((resolvedCoords: GeolocationCoords) => {
    saveCoordsToCache(resolvedCoords);
    setCoords(resolvedCoords);
  }, []);

  const handleServiceSelect = useCallback(
    (service: StorefrontService) => {
      navigate(`/vitrine/${service.id}?radius=${filters.radius}`);
    },
    [navigate, filters.radius],
  );

  const handleFiltersChange = useCallback(
    (updates: Partial<typeof filters>) => {
      setFilters(updates);
    },
    [setFilters],
  );

  return (
    <>
    <AppHeader variant="dark" />
    <Box sx={{ height: '100vh', pt: '72px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 2 },
          bgcolor: '#f8fcff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#00b4e4">
          Serviços Adicionais
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Serviços especiais disponíveis para assinantes WashPass na sua região
        </Typography>
      </Box>

      {!coords ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <LocationGate onLocationResolved={handleLocationResolved} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <StorefrontFilters
            filters={filters}
            onChange={handleFiltersChange}
            resultsCount={filteredServices.length}
          />

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <ServiceList
              services={filteredServices}
              loading={loading}
              error={error}
              onServiceSelect={handleServiceSelect}
            />
          </Box>
        </Box>
      )}
    </Box>
    </>
  );
}

export default VitrinePage;
